import { useState, type ReactNode } from 'react';
import { Divider } from '@understory-io/leaf/divider';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { ChevronDownIcon } from '~/ui/icons/chevron-down';
import {
  EventTime,
  formatSessionDateTime,
  isMultiDaySession,
} from '~/ui/event-time';
import { FormattedLocation } from '~/ui/formatted';
import type { LocationAddress } from '~/domain/availability/locations.server';

export type DisclosureSession = {
  startTime: string;
  endTime?: string | null;
  /** Optional — info-step sessions carry no location and render date+time only. */
  location?: LocationAddress | null;
};

type SessionDetailsDisclosureProps = {
  /** Sessions in start-time order. Each row shows its own date (sessions may
   *  fall on different dates), the time range, and the location when present. */
  sessions: DisclosureSession[];
  /** Disable the toggle (e.g. a sold-out time slot). */
  disabled?: boolean;
  /** Element rendered on the toggle's row, right-aligned (e.g. an availability
   *  label on the EventCard card). Omit on the info step. */
  trailing?: ReactNode;
};

/**
 * Uncontrolled "Session details" disclosure: a caret toggle that unfolds the
 * per-session date/time/location list. Extracted from the multi-session
 * EventCard card and reused on the checkout info step (desktop + mobile) so the
 * affordance is identical everywhere.
 */
export function SessionDetailsDisclosure({
  sessions,
  disabled = false,
  trailing,
}: SessionDetailsDisclosureProps) {
  const [expanded, setExpanded] = useState(false);
  const intl = useIntl();

  return (
    <Stack gap="xsmall">
      <Stack direction="row" gap="small" align="center" justify="space-between">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className={buttonStyle}
        >
          <Text as="span" style="caption">
            <FormattedMessage
              defaultMessage="Session details"
              description="Expand toggle on a multi-session time slot, reveals each session's date/time/location"
            />
          </Text>
          <span
            aria-hidden="true"
            className={cx(chevronStyle, expanded && chevronExpandedStyle)}
          >
            <ChevronDownIcon />
          </span>
        </button>
        {trailing}
      </Stack>

      {expanded && <Divider />}

      {expanded && (
        <Stack as="ul" gap="xsmall" className={listStyle}>
          {sessions.map((session, index) => {
            // Multi-day sessions carry their times inline with each date
            // (e.g. "5 September 10:00 – 6 September 16:00"), so the separate
            // time-only line below would be redundant — render it only for
            // single-day sessions (US-10838).
            const multiDay = isMultiDaySession(session);
            return (
              <li key={`${session.startTime}-${index}`}>
                <Stack gap="micro">
                  <Text
                    style="body-small-regular"
                    className={cx(timeTextStyle, dateCapStyle)}
                  >
                    {formatSessionDateTime(session, intl)}
                  </Text>
                  {(!multiDay || session.location) && (
                    <Stack
                      direction="row"
                      gap="small"
                      align="center"
                      wrap
                      className={css({ rowGap: 'micro' })}
                    >
                      {!multiDay && (
                        <Text style="caption" className={secondaryStyle}>
                          <EventTime
                            sessions={[
                              {
                                startTime: session.startTime,
                                endTime: session.endTime,
                              },
                            ]}
                          >
                            {({ details }) => details}
                          </EventTime>
                        </Text>
                      )}
                      {session.location && (
                        <Text style="caption" className={secondaryStyle}>
                          <FormattedLocation location={session.location} />
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              </li>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

// Replicates the former `eventCard` recipe slots (sessionDetailsButton /
// chevron / expandedSessions) so the disclosure carries its own styling.
const buttonStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'micro',
  // Keep the toggle clickable above the multi-session EventCard card's
  // stretched select overlay (see the eventCard recipe's multi variant).
  // Inert in contexts without an overlay (e.g. the checkout info step).
  position: 'relative',
  zIndex: '[1]',
  color: 'content.primary',
  cursor: 'interactive',
  textDecoration: 'underline',
  textUnderlineOffset: '[3px]',
  background: '[transparent]',
  border: 'none',
  padding: 'none',
  font: '[inherit]',
  _hover: { color: 'content.link' },
  _disabled: {
    cursor: 'disabled',
    color: 'content.tertiary',
    textDecoration: 'none',
    _hover: { color: 'content.tertiary' },
  },
  _focusVisible: {
    outline: 'medium',
    outlineColor: 'border.focus',
    outlineOffset: 'tiny',
    borderRadius: 'small',
  },
});

const chevronStyle = css({
  display: 'inline-flex',
  transition: 'transform',
  '@media (prefers-reduced-motion: reduce)': { transition: '[none]' },
});

const chevronExpandedStyle = css({ transform: 'rotate(180deg)' });

const listStyle = css({ listStyle: 'none', margin: 'none', padding: 'none' });

const timeTextStyle = css({ color: 'content.primary' });

const secondaryStyle = css({ color: 'content.secondary' });

const dateCapStyle = css({
  '&::first-letter': { textTransform: 'capitalize' },
});
